import { format, isAfter } from "date-fns";
import { capitalize } from "lodash";
import * as React from "react";
import { useSelector } from "react-redux";

import { Button, DesignToken, Text } from "@livechat/design-system-react-components";

import { DateFormat } from "constants/date";
import { getPlanName } from "helpers/subscription-plans";
import { SubscriptionChangePreview } from "routes/subscription/SubscriptionPlans/plan-summary/SubscriptionChangePreview";
import { getRecurlyCoupon, getRecurlyNewCoupon } from "store/entities/recurly";
import { BillingCycle, ISubscription, selectCurrentSubscription } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

import { CouponTooltip } from "../../components/coupon-tooltip/CouponTooltip";
import { useTaxDetails } from "../../contexts/tax-details-context/TaxDetailsContext";

import { StyledRow, StyledRowName, StyledRowValue, StyledWrapper } from "./styles";

interface IProps {
  newSubscription: ISubscription;
  isSavingCreditCard: boolean;
  onSubscribe(e: React.MouseEvent): void;
}

const periodLabelFromCycle = (cycle: BillingCycle, adjective = true): string => {
  switch (cycle) {
    case BillingCycle.Monthly:
      return adjective ? "monthly" : "month";
    case BillingCycle.Quarterly:
      return adjective ? "quarterly" : "quarter";
    case BillingCycle.SemiAnnually:
      return adjective ? "semi-annually" : "half year";
    case BillingCycle.Annually:
      return adjective ? "yearly" : "year";
    case BillingCycle.TwoYears:
      return "2 years";
    case BillingCycle.ThreeYears:
      return "3 years";
    default:
      return "custom";
  }
};

function calculateNextPaymentDate(trialExpiresAt: Date | null, isCanceled: boolean): string {
  const chargeDate =
    trialExpiresAt && (isAfter(new Date(), trialExpiresAt) || isCanceled) ? new Date() : (trialExpiresAt ?? new Date());
  return format(chargeDate, DateFormat.DateWithoutDayOfTheWeek);
}

export const PricingSummary: React.FC<IProps> = ({ newSubscription, isSavingCreditCard, onSubscribe }) => {
  const { country, zipCode } = useTaxDetails();
  const subscription = useSelector(selectCurrentSubscription);
  const newCoupon = useSelector(getRecurlyNewCoupon);
  const currentCoupon = useSelector(getRecurlyCoupon);
  const coupon = newCoupon || currentCoupon;
  const { billingCycle, trialExpiresAt, isCanceled, agents, plan } = newSubscription;

  return (
    <StyledWrapper>
      <StyledRow>
        <Text>Total agents</Text>
        <Text bold>{agents}</Text>
      </StyledRow>

      <StyledRow>
        <Text>Plan</Text>
        <Text bold>{getPlanName(plan)}</Text>
      </StyledRow>

      <StyledRow>
        <Text>Billing cycle</Text>
        <Text bold>{capitalize(periodLabelFromCycle(billingCycle))}</Text>
      </StyledRow>

      {coupon && (
        <StyledRow>
          <StyledRowName>
            Coupon
            <CouponTooltip coupon={coupon} />
          </StyledRowName>
          <StyledRowValue>{coupon.code}</StyledRowValue>
        </StyledRow>
      )}

      <StyledRow>
        <SubscriptionChangePreview
          subscription={subscription}
          newSubscription={newSubscription}
          coupon={coupon}
          countryCode={country}
          zipCode={zipCode}
        />
      </StyledRow>

      <div>
        <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
          Your card will be automatically charged every {periodLabelFromCycle(billingCycle, false)} beginning{" "}
          <b>{calculateNextPaymentDate(trialExpiresAt, isCanceled)}</b>.
        </Text>
      </div>

      <Button kind="primary" loading={isSavingCreditCard} onClick={onSubscribe}>
        Subscribe now
      </Button>
    </StyledWrapper>
  );
};
